import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client';
import { merge } from 'lodash';
import { ReactElement, useEffect, useState } from 'react';
import { ApiProviderProps, getCommonApiHeaders } from '../../api/api.provider';
import { createApolloClient } from '../../api/apollo.client';
import { AttachmentCacheTypePolicies } from '../../api/cache/attachment.cache';
import { MachineCacheTypePolicies } from '../../api/cache/machine.cache';
import { ScratchpadCacheTypePolicies } from '../../api/cache/scratchpad.cache';
import { ApolloSubscriptionManagerProvider } from '../../api/subscription/apollo-subscription.manager';
import { useUserProvider } from '../../api/user.provider';
import { runInBackground } from '../../util/async';
import { useDirectConnectSessionProvider } from './direct-connect-session.provider';

interface DirectConnectAuthorizationHeaders {
  'x-user-id': string;
  'x-tenant-id': string;
  'x-session-id': string;
}

const getDirectConnectAuthorizationHeaders = (options: {
  tenantId?: string;
  userId?: string;
  sessionId?: string;
}): DirectConnectAuthorizationHeaders => {
  const { tenantId = '', userId = '', sessionId = '' } = options;

  const headers: DirectConnectAuthorizationHeaders = {
    'x-user-id': userId,
    'x-tenant-id': tenantId,
    'x-session-id': sessionId,
  };

  return headers;
};

const createCache = async (): Promise<InMemoryCache> => {
  return new InMemoryCache({
    typePolicies: merge(
      AttachmentCacheTypePolicies,
      MachineCacheTypePolicies,
      ScratchpadCacheTypePolicies
    ),
  });
};

const DirectConnectApiProvider = (
  props: ApiProviderProps
): ReactElement | null => {
  const {
    apollo: { uri },
  } = props;

  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  const [apolloClient, setApolloClient] =
    useState<ApolloClient<NormalizedCacheObject>>();

  const sessionProvider = useDirectConnectSessionProvider();

  // In the order of execution of the gates, this api provider
  // is first then the other gates are instantiated. Not until
  // the health and user gates run is the user info populated.
  // So know that the user info will be nil or incomplete and
  // require another rerender to get the latest user info.
  // Either recreate the apollo client when this changes or
  // put the user info in a ref and reference it that way.
  const { userInfo } = useUserProvider();

  useEffect(() => {
    runInBackground(async () => {
      const client = await createApolloClient({
        url: uri,
        cacheProvider: async () => {
          const cache = await createCache();

          return {
            cacheName: `apollo-direct-cache`,
            cache,
          };
        },
        headerProvider: async () => {
          const commonHeaders = getCommonApiHeaders();

          let authorizationHeaders = {};

          const session = await sessionProvider();

          authorizationHeaders = getDirectConnectAuthorizationHeaders({
            ...userInfo,
            sessionId: session?.sessionId,
          });

          return {
            ...authorizationHeaders,
            ...commonHeaders,
          };
        },
      });
      setApolloClient(client);
      setIsInitialized(true);
    });
  }, [uri, userInfo, sessionProvider]);

  if (isInitialized && apolloClient) {
    return (
      <ApolloProvider client={apolloClient}>
        <ApolloSubscriptionManagerProvider>
          {props.children}
        </ApolloSubscriptionManagerProvider>
      </ApolloProvider>
    );
  }

  return null;
};

export { DirectConnectApiProvider };
